import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useAtom } from 'jotai';
import { settingsAtom } from '../utils/atoms';
import api from '../utils/requests/base';

const REPORT_TYPES = [
    { value: 'income', label: 'דו"ח הכנסות' },
    { value: 'maam', label: 'דו"ח מע"מ' },
];

const DELIVERY_OPTIONS = {
    income: [{ value: 'email', label: 'שלח למייל' }],
    maam: [
        { value: 'email', label: 'שלח למייל' },
        { value: 'download', label: 'הורד מיידית' },
    ],
};

const ReportModal = ({ show, onClose, startDate, endDate, initialType }) => {

    const [settings] = useAtom(settingsAtom)

    const [step, setStep] = useState(1);
    const [reportType, setReportType] = useState(initialType || 'income');
    const [delivery, setDelivery] = useState('email');
    const [email, setEmail] = useState(settings?.user?.email || '');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [downloadUrl, setDownloadUrl] = useState(null);

    const handleNext = () => setStep(2);
    const handleBack = () => setStep(1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess(false);
        setDownloadUrl(null);
        try {
            if (reportType === 'income') {
                await api.post(`/teacher/invoices/reports/income`, {
                    startDate: startDate.getTime(),
                    endDate: endDate.getTime(),
                    email,
                });
                setSuccess(true);
            } else if (reportType === 'maam') {
                if (delivery === 'download') {
                    const res = await api.post(
                        `/teacher/invoices/reports/maam`,
                        {
                            startDate: startDate.getTime(),
                            endDate: endDate.getTime(),
                        },
                    );
                    // Expecting res.data.documentUrl
                    if (res.data && res.data.documentUrl) {
                        setDownloadUrl(res.data.documentUrl);
                        setSuccess(true);
                    } else {
                        setError('לא התקבל קובץ להורדה');
                    }
                } else {
                    await api.post(`/teacher/invoices/reports/maam`, {
                        startDate: startDate.getTime(),
                        endDate: endDate.getTime(),
                        email,
                    });
                    setSuccess(true);
                }
            }
        } catch (err) {
            setError('אירעה שגיאה בשליחת הדו״ח.');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (downloadUrl) {
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'maam-report.pdf');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        }
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.3)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">הפקת דו״ח</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {success ? (
                            <div className="text-center py-4">
                                <Icon icon="mdi:check-circle" className="text-success mb-2" width={48} height={48} />
                                <div className="fw-bold mb-2">{delivery === 'download' ? "הדו״ח שלך מוכן!" : "אנחנו מייצרים לך את הדו״ח"}</div>
                                {delivery === 'email' && (
                                    <div>אנחנו מפיקים את הדו״ח ושולחים אותו לכתובת <b>{email}</b></div>
                                )}
                                {delivery === 'download' && downloadUrl && (
                                    <div className="mt-3">
                                        <button className="btn btn-primary" onClick={handleDownload}>
                                            הורד דו״ח מע״מ
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="mt-3">מפיקים את הדו״ח...</div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {step === 1 && (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label">בחר סוג דו״ח:</label>
                                            <div className="d-flex gap-3">
                                                {REPORT_TYPES.map(rt => (
                                                    <button
                                                        type="button"
                                                        key={rt.value}
                                                        className={`btn btn-outline-primary${reportType === rt.value ? ' active' : ''}`}
                                                        onClick={() => setReportType(rt.value)}
                                                    >
                                                        {rt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-end">
                                            <button type="button" className="btn btn-primary" onClick={handleNext}>
                                                המשך
                                            </button>
                                        </div>
                                    </>
                                )}
                                {step === 2 && (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label">בחר אופן קבלת הדו״ח:</label>
                                            <div className="d-flex gap-3">
                                                {DELIVERY_OPTIONS[reportType].map(opt => (
                                                    <button
                                                        type="button"
                                                        key={opt.value}
                                                        className={`btn btn-outline-primary${delivery === opt.value ? ' active' : ''}`}
                                                        onClick={() => setDelivery(opt.value)}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        {!(reportType === 'maam' && delivery === 'download') && (
                                            <div className="mb-3">
                                                <label className="form-label">כתובת מייל:</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    value={email}
                                                    onChange={e => setEmail(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        )}
                                        {error && <div className="alert alert-danger">{error}</div>}
                                        <div className="d-flex justify-content-between">
                                            <button type="button" className="btn btn-link" onClick={handleBack}>
                                                חזור
                                            </button>
                                            <button type="submit" className="btn btn-primary">
                                                {delivery === 'download' ? 'הורד דו״ח' : 'שלח דו״ח'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportModal; 